import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Send, Trash2, Activity, Sun, Moon } from 'lucide-react';
import ChakanaIcon from './ChakanaIcon';
import { sendChatMessage } from '../services/api';
import ServiceStatusCard from './apu/ServiceStatusCard';
import CleanerPerformanceCard from './apu/CleanerPerformanceCard';

/* ── Structured component registry ───────────────────────────────── */
const STRUCTURED_COMPONENTS = {
  service_status: ServiceStatusCard,
  cleaner_performance: CleanerPerformanceCard,
};

const parseStructuredContent = (text) => {
  const jsonMatch = text.match(/```json\s*\n([\s\S]*?)\n```/);
  if (!jsonMatch) return { structured: null, prose: text };
  try {
    const data = JSON.parse(jsonMatch[1]);
    if (!data.type) return { structured: null, prose: text };
    const prose = text.replace(/```json\s*\n[\s\S]*?\n```/, '').trim();
    return { structured: data, prose };
  } catch {
    return { structured: null, prose: text };
  }
};

/* ── Palettes — Dark (Andean night) & Light (Andean dawn) ─────────── */
const DARK = {
  accent: '#c9a86c',
  accentBright: '#dbb878',
  accentDim: 'rgba(201, 168, 108, 0.14)',
  text: '#ddd5ca',
  textBright: '#f0e8de',
  textMuted: '#6b5e50',
  border: 'rgba(201, 168, 108, 0.10)',
  borderActive: 'rgba(201, 168, 108, 0.32)',
  userBg: 'rgba(201, 168, 108, 0.06)',
  userBorder: 'rgba(201, 168, 108, 0.16)',
  statusGreen: '#7bc47f',
  tableBorder: 'rgba(201, 168, 108, 0.08)',
  tableHeader: 'rgba(201, 168, 108, 0.06)',
  tableStripe: 'rgba(201, 168, 108, 0.02)',
  overlayBg: `
    radial-gradient(ellipse 80% 45% at 50% 100%, rgba(201, 168, 108, 0.12) 0%, transparent 70%),
    radial-gradient(ellipse 50% 30% at 50% 100%, rgba(201, 168, 108, 0.06) 0%, transparent 50%),
    linear-gradient(180deg, #0a0a10 0%, #0e0c14 40%, #100e18 100%)
  `,
  headerBg: 'linear-gradient(180deg, rgba(10, 8, 16, 0.6) 0%, transparent 100%)',
  inputAreaBg: 'linear-gradient(0deg, rgba(10, 8, 16, 0.5) 0%, transparent 100%)',
  inputBarBg: 'rgba(201, 168, 108, 0.03)',
  sendBg: 'rgba(201, 168, 108, 0.12)',
  sendHover: 'rgba(201, 168, 108, 0.22)',
  hintBg: 'rgba(201, 168, 108, 0.03)',
  hintHover: 'rgba(201, 168, 108, 0.07)',
  scrollThumb: 'rgba(201, 168, 108, 0.15)',
  scrollHover: 'rgba(201, 168, 108, 0.3)',
  orbBg: 'radial-gradient(circle at 45% 40%, rgba(219, 184, 120, 0.9) 0%, rgba(201, 168, 108, 0.3) 35%, rgba(201, 168, 108, 0.08) 60%, transparent 80%)',
  orbIcon: '#fff',
};

const LIGHT = {
  accent: '#9c7d3c',
  accentBright: '#7a6230',
  accentDim: 'rgba(156, 125, 60, 0.08)',
  text: '#3a3a3a',
  textBright: '#1a1a1a',
  textMuted: '#999',
  border: 'rgba(0, 0, 0, 0.08)',
  borderActive: 'rgba(156, 125, 60, 0.25)',
  userBg: 'rgba(0, 0, 0, 0.03)',
  userBorder: 'rgba(0, 0, 0, 0.06)',
  statusGreen: '#5a9e5e',
  tableBorder: 'rgba(0, 0, 0, 0.06)',
  tableHeader: 'rgba(0, 0, 0, 0.02)',
  tableStripe: 'rgba(0, 0, 0, 0.015)',
  overlayBg: `
    radial-gradient(ellipse 80% 45% at 50% 100%, rgba(201, 168, 108, 0.08) 0%, transparent 70%),
    linear-gradient(180deg, #ffffff 0%, #fafafa 100%)
  `,
  headerBg: 'linear-gradient(180deg, rgba(255, 255, 255, 0.9) 0%, transparent 100%)',
  inputAreaBg: 'linear-gradient(0deg, rgba(255, 255, 255, 0.8) 0%, transparent 100%)',
  inputBarBg: 'rgba(0, 0, 0, 0.02)',
  sendBg: 'rgba(156, 125, 60, 0.10)',
  sendHover: 'rgba(156, 125, 60, 0.18)',
  hintBg: 'rgba(0, 0, 0, 0.02)',
  hintHover: 'rgba(0, 0, 0, 0.04)',
  scrollThumb: 'rgba(0, 0, 0, 0.10)',
  scrollHover: 'rgba(0, 0, 0, 0.18)',
  orbBg: 'radial-gradient(circle at 45% 40%, rgba(201, 168, 108, 0.5) 0%, rgba(201, 168, 108, 0.15) 35%, rgba(201, 168, 108, 0.03) 60%, transparent 80%)',
  orbIcon: '#5c4a24',
};

/* ── Tool labels ──────────────────────────────────────────────────── */
const TOOL_LABELS = {
  get_today_operations: 'today_operations',
  get_property_reservations: 'reservations',
  get_cleaner_workload: 'cleaner_workload',
  get_lock_status: 'lock_status',
  get_verification_history: 'verifications',
  get_cleaner_performance: 'cleaner_metrics',
  get_property_analytics: 'property_analytics',
  find_anomalies: 'anomaly_scan',
  what_if: 'scenario_engine',
  generate_report: 'report_gen',
};

const ToolBadges = ({ tools, P }) => {
  if (!tools || tools.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginBottom: '10px', paddingLeft: '2px' }}>
      {tools.map((t, i) => (
        <span key={i} style={{
          fontSize: '11px', fontFamily: '"SF Mono", "Fira Code", "Cascadia Code", monospace',
          color: P.accent, opacity: 0.6, letterSpacing: '0.5px',
          display: 'inline-flex', alignItems: 'center', gap: '5px',
        }}>
          <span style={{ opacity: 0.8 }}>{'▸'}</span>
          {TOOL_LABELS[t.tool] || t.tool}
          <span style={{ color: P.textMuted, fontSize: '10px' }}>OK</span>
        </span>
      ))}
    </div>
  );
};

const STORAGE_KEY = 'ayni-chat-history';

/* ── Table parser helpers ─────────────────────────────────────────── */
const isTableRow = (line) => {
  const trimmed = line.trim();
  return trimmed.startsWith('|') && trimmed.endsWith('|') && trimmed.split('|').length >= 3;
};
const isTableSeparator = (line) => /^\|[\s:|-]+\|$/.test(line.trim()) && line.includes('-');
const parseTableCells = (line) => line.trim().split('|').slice(1, -1).map(cell => cell.trim());

const renderTable = (rows, keyPrefix, inlineFormat, P) => {
  if (rows.length < 2) return null;
  const headerRow = parseTableCells(rows[0]);
  const hasSeparator = rows.length >= 3 && isTableSeparator(rows[1]);
  const bodyRows = (hasSeparator ? rows.slice(2) : rows.slice(1)).map(r => parseTableCells(r));

  return (
    <div key={keyPrefix} style={{
      overflowX: 'auto', margin: '10px 0', borderRadius: '8px',
      border: `1px solid ${P.tableBorder}`,
    }}>
      <table style={{
        width: '100%', borderCollapse: 'collapse', fontSize: '13px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}>
        <thead>
          <tr>
            {headerRow.map((h, j) => (
              <th key={j} style={{
                padding: '8px 12px', textAlign: 'left',
                borderBottom: `1px solid ${P.borderActive}`,
                backgroundColor: P.tableHeader,
                color: P.accentBright, fontWeight: 600,
                fontSize: '11px', letterSpacing: '0.4px',
                textTransform: 'uppercase', whiteSpace: 'nowrap',
              }}>{inlineFormat(h)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {bodyRows.map((row, ri) => (
            <tr key={ri} style={{ backgroundColor: ri % 2 === 1 ? P.tableStripe : 'transparent' }}>
              {row.map((cell, ci) => (
                <td key={ci} style={{
                  padding: '7px 12px', borderBottom: `1px solid ${P.tableBorder}`,
                  color: P.text, fontSize: '13px', whiteSpace: ci === 0 ? 'nowrap' : 'normal',
                }}>{inlineFormat(cell)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ── Markdown renderer ────────────────────────────────────────────── */
const renderMarkdown = (text, P) => {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let listItems = [];
  let listType = null;
  let tableRows = [];

  const SEVERITY_PILLS = {
    CRITICAL: { bg: 'rgba(239,68,68,0.12)', color: '#ef4444', border: 'rgba(239,68,68,0.2)' },
    HIGH:     { bg: 'rgba(249,115,22,0.12)', color: '#f97316', border: 'rgba(249,115,22,0.2)' },
    MEDIUM:   { bg: 'rgba(234,179,8,0.12)',  color: '#ca8a04', border: 'rgba(234,179,8,0.2)' },
    LOW:      { bg: 'rgba(34,197,94,0.12)',  color: '#22c55e', border: 'rgba(34,197,94,0.2)' },
  };

  const inlineFormat = (str) => {
    if (typeof str !== 'string') return str;
    const parts = [];
    const regex = /(\[(?:CRITICAL|HIGH|MEDIUM|LOW)\]|\*\*\*(.+?)\*\*\*|\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`)/g;
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(str)) !== null) {
      if (match.index > lastIndex) parts.push(str.slice(lastIndex, match.index));
      const sevMatch = match[0].match(/^\[(CRITICAL|HIGH|MEDIUM|LOW)\]$/);
      if (sevMatch) {
        const sev = SEVERITY_PILLS[sevMatch[1]];
        parts.push(
          <span key={match.index} style={{
            display: 'inline-block', fontSize: '10px', fontWeight: 700, padding: '2px 8px',
            borderRadius: '10px', backgroundColor: sev.bg, color: sev.color,
            border: `1px solid ${sev.border}`, letterSpacing: '0.5px', marginRight: '6px',
            verticalAlign: 'middle', lineHeight: '1.4',
          }}>{sevMatch[1]}</span>
        );
      }
      else if (match[2]) parts.push(<strong key={match.index}><em>{match[2]}</em></strong>);
      else if (match[3]) parts.push(<strong key={match.index} style={{ color: P.textBright, fontWeight: 600 }}>{match[3]}</strong>);
      else if (match[4]) parts.push(<em key={match.index}>{match[4]}</em>);
      else if (match[5]) parts.push(
        <code key={match.index} style={{
          backgroundColor: P.accentDim, padding: '1px 6px', borderRadius: '4px', fontSize: '12px',
          fontFamily: '"SF Mono", "Fira Code", monospace',
          color: P.accentBright, border: `1px solid ${P.accentDim}`,
        }}>{match[5]}</code>
      );
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < str.length) parts.push(str.slice(lastIndex));
    return parts.length > 0 ? parts : str;
  };

  const flushList = () => {
    if (listItems.length > 0) {
      const Tag = listType === 'ol' ? 'ol' : 'ul';
      elements.push(
        <Tag key={`list-${elements.length}`} style={{
          margin: '6px 0', paddingLeft: '18px', fontSize: '14px', lineHeight: '1.7', color: P.text,
        }}>
          {listItems.map((li, j) => <li key={j} style={{ marginBottom: '3px' }}>{inlineFormat(li)}</li>)}
        </Tag>
      );
      listItems = [];
      listType = null;
    }
  };

  const flushTable = () => {
    if (tableRows.length >= 2) {
      const table = renderTable(tableRows, `table-${elements.length}`, inlineFormat, P);
      if (table) elements.push(table);
    } else {
      tableRows.forEach((row, i) => {
        elements.push(<div key={`tr-${elements.length}-${i}`} style={{ margin: '2px 0' }}>{inlineFormat(row)}</div>);
      });
    }
    tableRows = [];
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (isTableRow(line)) { flushList(); tableRows.push(line); continue; }
    if (tableRows.length > 0) flushTable();
    const ulMatch = line.match(/^[\s]*[-•]\s+(.*)/);
    if (ulMatch) { if (listType === 'ol') flushList(); listType = 'ul'; listItems.push(ulMatch[1]); continue; }
    const olMatch = line.match(/^[\s]*\d+[.)]\s+(.*)/);
    if (olMatch) { if (listType === 'ul') flushList(); listType = 'ol'; listItems.push(olMatch[1]); continue; }
    flushList();
    if (line.trim() === '') { elements.push(<div key={`br-${i}`} style={{ height: '8px' }} />); continue; }
    const headingMatch = line.match(/^(#{1,3})\s+(.*)/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const fontSize = level === 1 ? '17px' : level === 2 ? '15px' : '14px';
      elements.push(
        <div key={`h-${i}`} style={{
          fontSize, fontWeight: 600, margin: '12px 0 4px', color: P.textBright, letterSpacing: '-0.2px',
        }}>{inlineFormat(headingMatch[2])}</div>
      );
      continue;
    }
    elements.push(<div key={`p-${i}`} style={{ margin: '2px 0' }}>{inlineFormat(line)}</div>);
  }
  if (tableRows.length > 0) flushTable();
  flushList();
  return elements;
};

/* ── Styles factory ───────────────────────────────────────────────── */
const getStyles = (P) => ({
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    zIndex: 9999, display: 'flex', flexDirection: 'column',
    background: P.overlayBg,
    backdropFilter: 'blur(30px) saturate(1.3)',
    WebkitBackdropFilter: 'blur(30px) saturate(1.3)',
    transition: 'opacity 0.3s ease',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 32px', borderBottom: `1px solid ${P.border}`,
    flexShrink: 0, background: P.headerBg, position: 'relative', zIndex: 2,
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  headerTitle: {
    fontSize: '14px', fontWeight: 600, color: P.accent, letterSpacing: '2px',
    textTransform: 'uppercase', fontFamily: '"SF Mono", "Fira Code", "Cascadia Code", monospace',
  },
  headerSub: {
    fontSize: '10px', color: P.textMuted,
    fontFamily: '"SF Mono", "Fira Code", monospace', letterSpacing: '0.5px',
    textTransform: 'uppercase', marginLeft: '8px', opacity: 0.7,
  },
  headerStatus: {
    display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px', color: P.textMuted,
    fontFamily: '"SF Mono", "Fira Code", monospace', letterSpacing: '0.5px', textTransform: 'uppercase',
  },
  statusDot: {
    width: 6, height: 6, borderRadius: '50%', backgroundColor: P.statusGreen,
    boxShadow: `0 0 8px rgba(123, 196, 127, 0.5)`,
  },
  headerActions: { display: 'flex', alignItems: 'center', gap: '4px' },
  iconBtn: {
    width: 40, height: 40, borderRadius: '8px', border: 'none', backgroundColor: 'transparent',
    color: P.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center',
    justifyContent: 'center', transition: 'all 0.15s ease',
  },
  messagesContainer: {
    flex: 1, overflowY: 'auto', display: 'flex', justifyContent: 'center',
    position: 'relative', zIndex: 2,
  },
  contentColumn: { width: '100%', maxWidth: 740, padding: '24px 28px' },
  welcome: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', padding: '80px 20px 40px', textAlign: 'center',
  },
  welcomeOrb: {
    width: 80, height: 80, borderRadius: '50%', background: P.orbBg,
    display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '28px',
    border: `1px solid rgba(201, 168, 108, 0.25)`,
  },
  welcomeTitle: {
    fontSize: '14px', fontWeight: 600, color: P.accent, marginBottom: '6px',
    letterSpacing: '4px', textTransform: 'uppercase', fontFamily: '"SF Mono", "Fira Code", monospace',
  },
  welcomeSubtitle: {
    fontSize: '11px', color: P.textMuted, letterSpacing: '1.5px', textTransform: 'uppercase',
    fontFamily: '"SF Mono", "Fira Code", monospace', marginBottom: '12px',
  },
  welcomeDesc: { fontSize: '14px', color: P.textMuted, lineHeight: '1.6', maxWidth: '400px' },
  welcomeHints: {
    marginTop: '32px', display: 'grid', gridTemplateColumns: '1fr 1fr',
    gap: '8px', width: '100%', maxWidth: '500px',
  },
  welcomeHint: {
    padding: '12px 14px', borderRadius: '10px', border: `1px solid ${P.border}`,
    backgroundColor: P.hintBg, color: P.text, fontSize: '13px', cursor: 'pointer',
    textAlign: 'left', transition: 'all 0.2s ease',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    display: 'flex', alignItems: 'center', gap: '8px', lineHeight: '1.4',
  },
  hintArrow: { color: P.accent, opacity: 0.4, flexShrink: 0, fontSize: '12px', fontFamily: 'monospace' },
  userBubble: {
    maxWidth: '75%', marginLeft: 'auto', marginBottom: 18, padding: '10px 16px',
    borderRadius: '16px 16px 4px 16px', backgroundColor: P.userBg,
    border: `1px solid ${P.userBorder}`, color: P.text, fontSize: '14px',
    lineHeight: '1.5', wordBreak: 'break-word',
  },
  assistantBubble: {
    maxWidth: '100%', marginRight: 'auto', marginBottom: 20, padding: '4px 0 4px 14px',
    borderLeft: `2px solid ${P.accentDim}`, color: P.text, fontSize: '14px',
    lineHeight: '1.65', wordBreak: 'break-word',
  },
  loadingArea: {
    maxWidth: '90%', marginRight: 'auto', marginBottom: 16,
    padding: '12px 0 12px 14px', borderLeft: `2px solid ${P.accent}`,
  },
  errorBanner: {
    maxWidth: 740, margin: '0 auto 8px', padding: '8px 14px', borderRadius: '8px',
    backgroundColor: 'rgba(180, 80, 60, 0.12)', border: '1px solid rgba(180, 80, 60, 0.20)',
    color: '#d4806a', fontSize: '12px', fontWeight: 500,
    fontFamily: '"SF Mono", "Fira Code", monospace', position: 'relative', zIndex: 2,
  },
  inputArea: {
    padding: '14px 28px 24px', flexShrink: 0, borderTop: `1px solid ${P.border}`,
    background: P.inputAreaBg, display: 'flex', justifyContent: 'center',
    position: 'relative', zIndex: 2,
  },
  inputBar: {
    display: 'flex', alignItems: 'flex-end', gap: 0, borderRadius: '14px',
    border: `1px solid ${P.border}`, backgroundColor: P.inputBarBg,
    padding: '4px 6px 4px 18px', transition: 'border-color 0.25s ease, box-shadow 0.25s ease',
    width: '100%', maxWidth: 740,
  },
  inputBarFocused: {
    borderColor: P.borderActive,
    boxShadow: `0 0 0 3px ${P.accentDim}, 0 0 20px rgba(201, 168, 108, 0.06)`,
  },
  textarea: {
    flex: 1, resize: 'none', border: 'none', padding: '10px 8px', fontSize: '14px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    backgroundColor: 'transparent', color: P.text, outline: 'none', maxHeight: 120, lineHeight: '1.5',
  },
  sendBtn: {
    width: 38, height: 38, borderRadius: '10px', border: `1px solid ${P.borderActive}`,
    background: P.sendBg, color: P.accent, cursor: 'pointer', display: 'flex',
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    transition: 'all 0.2s ease', marginBottom: '2px',
  },
  sendBtnDisabled: {
    width: 38, height: 38, borderRadius: '10px', border: `1px solid ${P.border}`,
    backgroundColor: 'transparent', color: P.textMuted, cursor: 'default',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, marginBottom: '2px', opacity: 0.4,
  },
});

const HINT_QUESTIONS = [
  "What's happening today?",
  'Any anomalies I should know about?',
  'Generate a weekly ops report',
  'Compare all cleaners',
];

/* ── Processing animation ─────────────────────────────────────────── */
const ProcessingIndicator = ({ S, P }) => {
  const [dots, setDots] = useState('');
  const [activeProcess, setActiveProcess] = useState(0);
  const processes = ['Querying systems', 'Analyzing data', 'Cross-referencing', 'Generating response'];

  useEffect(() => {
    const dotTimer = setInterval(() => setDots(prev => prev.length >= 3 ? '' : prev + '.'), 400);
    const processTimer = setInterval(() => setActiveProcess(prev => (prev + 1) % processes.length), 2200);
    return () => { clearInterval(dotTimer); clearInterval(processTimer); };
  }, []);

  return (
    <div style={S.loadingArea}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
        <Activity size={14} style={{ color: P.accent, animation: 'aynipulse 1.5s ease-in-out infinite' }} />
        <span style={{ fontSize: '13px', color: P.accent, fontWeight: 500 }}>Processing</span>
      </div>
      <div style={{
        fontSize: '11px', fontFamily: '"SF Mono", "Fira Code", monospace',
        color: P.textMuted, letterSpacing: '0.3px',
      }}>
        <span style={{ color: P.accent, opacity: 0.5 }}>{'>'}</span>{' '}
        {processes[activeProcess]}{dots}
      </div>
    </div>
  );
};

/* ── Main Component ───────────────────────────────────────────────── */
const ChatDrawer = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState(() => {
    try { const saved = localStorage.getItem(STORAGE_KEY); return saved ? JSON.parse(saved) : []; }
    catch { return []; }
  });
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [inputFocused, setInputFocused] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  /* Theme detection — syncs with App.js data-theme attribute */
  const [isDark, setIsDark] = useState(() =>
    (localStorage.getItem('ayni-theme') || 'light') === 'dark'
  );
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const t = document.documentElement.getAttribute('data-theme') || 'light';
      setIsDark(t === 'dark');
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  const P = isDark ? DARK : LIGHT;
  const S = getStyles(P);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(messages)); }
    catch { /* degrade silently */ }
  }, [messages]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);
  useEffect(() => { if (isOpen) setTimeout(() => inputRef.current?.focus(), 300); }, [isOpen]);
  useEffect(() => { if (error) { const t = setTimeout(() => setError(null), 5000); return () => clearTimeout(t); } }, [error]);
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape' && isOpen) onClose(); };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  const handleSend = useCallback(async (overrideText) => {
    const text = (overrideText || input).trim();
    if (!text || loading) return;
    setError(null);
    const userMsg = { role: 'user', content: text };
    const prevMessages = [...messages];
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    try {
      const result = await sendChatMessage(text, prevMessages);
      setMessages(prev => [...prev, { role: 'assistant', content: result.response, tools_used: result.tools_used || [] }]);
    } catch (err) {
      setError(err.response?.data?.error || 'Connection failed. Retry.');
      setMessages(prevMessages);
    } finally { setLoading(false); }
  }, [input, loading, messages]);

  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } };
  const handleClear = () => { setMessages([]); setError(null); localStorage.removeItem(STORAGE_KEY); };
  const handleInputChange = (e) => {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  };

  if (!isOpen) return null;

  return (
    <>
      <div style={S.overlay}>
        {/* Header */}
        <div style={S.header}>
          <div style={S.headerLeft}>
            <ChakanaIcon size={22} color={P.accent} />
            <span style={S.headerTitle}>APU</span>
            <span style={S.headerSub}>by AYNI</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={S.headerStatus}>
              <div style={S.statusDot} />
              Online
            </div>
            <div style={S.headerActions}>
              <button onClick={() => {
                const next = isDark ? 'light' : 'dark';
                localStorage.setItem('ayni-theme', next);
                document.documentElement.setAttribute('data-theme', next);
              }} style={S.iconBtn} title={isDark ? 'Light mode' : 'Dark mode'}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = P.accentDim; e.currentTarget.style.color = P.text; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = P.textMuted; }}>
                {isDark ? <Sun size={22} /> : <Moon size={22} />}
              </button>
              {messages.length > 0 && (
                <button onClick={handleClear} style={S.iconBtn} title="Clear"
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = P.accentDim; e.currentTarget.style.color = P.text; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = P.textMuted; }}>
                  <Trash2 size={22} />
                </button>
              )}
              <button onClick={() => onClose()} style={S.iconBtn} title="Close (Esc)"
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = P.accentDim; e.currentTarget.style.color = P.text; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = P.textMuted; }}>
                <X size={22} />
              </button>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div style={S.messagesContainer} className="ayni-scrollbar">
          <div style={S.contentColumn}>
            {messages.length === 0 && !loading && (
              <div style={S.welcome}>
                <div style={{ ...S.welcomeOrb, animation: 'orbGlow 4s ease-in-out infinite' }}>
                  <ChakanaIcon size={28} color={P.orbIcon} style={{ filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.4))' }} />
                </div>
                <div style={S.welcomeTitle}>APU</div>
                <div style={S.welcomeSubtitle}>AYNI Operations Intelligence</div>
                <div style={S.welcomeDesc}>
                  The guardian spirit of your operations. Ask anything about properties, cleaners, schedules, or performance.
                </div>
                <div style={S.welcomeHints}>
                  {HINT_QUESTIONS.map((q, i) => (
                    <button key={i} style={S.welcomeHint} onClick={() => handleSend(q)}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = P.borderActive; e.currentTarget.style.backgroundColor = P.hintHover; e.currentTarget.style.color = P.textBright; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = P.border; e.currentTarget.style.backgroundColor = P.hintBg; e.currentTarget.style.color = P.text; }}>
                      <span style={S.hintArrow}>{'>'}</span>{q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} style={{ ...(msg.role === 'user' ? S.userBubble : S.assistantBubble), animation: 'fadeInUp 0.3s ease-out' }}>
                {msg.role === 'assistant' && <ToolBadges tools={msg.tools_used} P={P} />}
                {msg.role === 'assistant' ? (() => {
                  const { structured, prose } = parseStructuredContent(msg.content);
                  const Component = structured ? STRUCTURED_COMPONENTS[structured.type] : null;
                  return (
                    <>
                      {Component && <Component data={structured.data} P={P} />}
                      {prose && renderMarkdown(prose, P)}
                    </>
                  );
                })() : msg.content}
              </div>
            ))}

            {loading && <ProcessingIndicator S={S} P={P} />}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Error */}
        {error && <div style={S.errorBanner}><span style={{ opacity: 0.6 }}>{'>'}</span> {error}</div>}

        {/* Input */}
        <div style={S.inputArea}>
          <div style={{ ...S.inputBar, ...(inputFocused ? S.inputBarFocused : {}) }}>
            <textarea
              ref={inputRef} value={input} onChange={handleInputChange} onKeyDown={handleKeyDown}
              onFocus={() => setInputFocused(true)} onBlur={() => setInputFocused(false)}
              placeholder="Ask APU..." rows={1} style={S.textarea} disabled={loading}
            />
            <button
              onClick={() => handleSend()} disabled={!input.trim() || loading}
              style={!input.trim() || loading ? S.sendBtnDisabled : S.sendBtn} title="Send"
              onMouseEnter={(e) => { if (input.trim() && !loading) { e.currentTarget.style.background = P.sendHover; e.currentTarget.style.boxShadow = `0 0 12px ${P.accentDim}`; } }}
              onMouseLeave={(e) => { if (input.trim() && !loading) { e.currentTarget.style.background = P.sendBg; e.currentTarget.style.boxShadow = 'none'; } }}>
              <Send size={15} />
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes aynipulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes orbGlow {
          0%, 100% { box-shadow: 0 0 60px rgba(201, 168, 108, 0.25), 0 0 120px rgba(201, 168, 108, 0.1), 0 0 200px rgba(201, 168, 108, 0.05); }
          50% { box-shadow: 0 0 80px rgba(201, 168, 108, 0.35), 0 0 150px rgba(201, 168, 108, 0.15), 0 0 250px rgba(201, 168, 108, 0.08); }
        }
        .ayni-scrollbar::-webkit-scrollbar { width: 5px; }
        .ayni-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .ayni-scrollbar::-webkit-scrollbar-thumb { background: ${P.scrollThumb}; border-radius: 4px; }
        .ayni-scrollbar::-webkit-scrollbar-thumb:hover { background: ${P.scrollHover}; }
      `}</style>
    </>
  );
};

export default ChatDrawer;
